import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { ViolationLevel, ViolationSource } from 'shared/violations/types'
import { Studio } from 'Core/Studio'
import { AIDetectorIcon } from 'Components/common/Icons/AIDetectorIcon'
import { checkForAIContentApi } from 'HTTPClients/plugins.client'
import { UpdatableReactElement, createReactElement } from 'shared/utils/react.utils'
import { convert } from 'html-to-text'
import { isUndefined } from 'lodash'
import { AIDetector } from 'Plugins/AIDetector/component/AIDetector'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
import { BlockStateFormatKey } from 'shared/express-interface/blocks-core/ITaskBlock'

export type DisabledStateType = {
    isDisabled: boolean
    reason: string
}

export type AIDetectorConfigType = {
    /**
     * how many times plugin can be used per task
     */
    limit: number
    minWordsLimit: number
    optimalWordsLimit: number
    /**
     * max % of ai text, above which we wont accept submission
     */
    maxThreshold: number
    pluginType: string
}

export type AIDetectorDetailsType = {
    percentage: number
    aiTexts: string[] | null
}

export class AIDetectorPlugin extends BasePlugin<PluginConfigType<AIDetectorConfigType>> {
    public readonly id = 'AI Detection'
    private uiElement?: UpdatableReactElement
    private aiDetectorDetails?: AIDetectorDetailsType
    private checksMade: number = 0
    private isResponseFitForSubmission: boolean = false

    constructor(
        details: PluginConfigType<AIDetectorConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(details, pluginConnectors)
    }

    public getAIContentPercentage(): number | undefined {
        return this.aiDetectorDetails?.percentage
    }

    public initialize(studio: Studio) {
        this.studio = studio
        studio.workbench?.enqueueUiUpdate(accessor => {
            this.uiElement = createReactElement(AIDetector, {
                onCheckForAIContent: async () => {
                    this.aiDetectorDetails = await this.checkForAIContent()
                    if (this.checksMade > 0) {
                        this.removeMandatoryViolation()
                    }
                    this.uiElement?.updater.forceUpdate()
                    if (this.aiDetectorDetails) {
                        this.checkViolations(this.aiDetectorDetails)
                    }
                },
                getAIDetectionDetails: this.getAIDetectionResponse,
                getDisabledState: this.getDisabledState,
                isResponseFitForSubmission: this.isResponseFitForSubmission,
                setIsResponseFitForSubmission: () => {
                    this.isResponseFitForSubmission = !this.isResponseFitForSubmission
                    this.checkViolations(this.aiDetectorDetails!)
                    this.uiElement?.updater.forceUpdate()
                },
                maxAIThreshold: this.config.config.maxThreshold,
            })
            accessor.rightSidebar.addItem({
                element: this.uiElement.element,
                title: 'AI Detector',
                icon: AIDetectorIcon,
                id: this.id,
            })
        })

        const data = this.getSelectedConnector().getData()
        if (data['text']) {
            data['text'].subscribe(() => {
                this.uiElement?.updater.forceUpdate()
            })
        }

        this.addMandatoryViolation()
    }

    public addMandatoryViolation() {
        this.studio?.inputManager.addViolation({
            id: `mandatory_plugin_${this.id}`,
            message: 'You need to run AI Detection check before submitting',
            type: 'error',
            source: ViolationSource.Plugin,
        })
    }

    public removeMandatoryViolation() {
        this.studio?.inputManager.removeViolation(`mandatory_plugin_${this.id}`, 'error')
    }

    public reset() {
        this.aiDetectorDetails = undefined
        this.isResponseFitForSubmission = false
        this.uiElement?.updater.forceUpdate()
    }

    private checkViolations(aiDetectorDetails: AIDetectorDetailsType) {
        this.pluginConnectors.forEach(connector => {
            const hasExceededLimit = this.config.config.maxThreshold < aiDetectorDetails.percentage

            const level: ViolationLevel = this.hasExceededLimit() ? 'warning' : 'error'

            if (hasExceededLimit && !this.isResponseFitForSubmission) {
                this.studio?.inputManager.removeViolation?.(`${connector.id}_ai`)
                this.studio?.inputManager.addViolation?.({
                    id: connector.id + '_ai',
                    type: level,
                    message: `AI Detection percentage (${aiDetectorDetails.percentage}%) is higher than the allowed threshold.`,
                    source: ViolationSource.Plugin,
                })
            } else {
                this.studio?.inputManager.removeViolation?.(`${connector.id}_ai`)
            }
        })
    }

    private readonly getDisabledState = (): DisabledStateType => {
        const parametersData = this.getParametersData()
        const data = parametersData['text']

        if (!data?.value) {
            return { isDisabled: true, reason: 'The response is empty' }
        }
        if (this.hasExceededLimit()) {
            return {
                isDisabled: true,
                reason: 'You have reached the maximum limit for AI Content checks',
            }
        }
        return { isDisabled: false, reason: '' }
    }

    private readonly getAIDetectionResponse = (): AIDetectorDetailsType | undefined => {
        return this.aiDetectorDetails
    }

    private readonly hasExceededLimit = (): boolean => {
        return this.checksMade >= this.config.config.limit
    }

    private handleViolation(words: number): boolean {
        if (isUndefined(words)) throw new Error('Words are Undefined')

        if (
            this.config.config.minWordsLimit !== undefined &&
            words < this.config.config?.minWordsLimit
        ) {
            this.studio?.workbench.enqueueUiUpdate(ui => {
                ui.notifier.notification.error({
                    message: `AI Detection word count is less than ${this.config.config?.minWordsLimit} words: ${words}`,
                })
            })
            return true
        } else if (
            this.config.config?.optimalWordsLimit !== undefined &&
            words < this.config.config?.optimalWordsLimit
        ) {
            this.studio?.workbench.enqueueUiUpdate(ui => {
                ui.notifier.notification.warning({
                    message: `For optimal results, AI Detection word count should be more than ${this.config.config?.optimalWordsLimit} words: ${words}.`,
                })
            })
            return false
        } else {
            this.studio?.inputManager?.removeViolation?.(
                `${this.getSelectedConnector().id}_${this.id}`,
                undefined
            )
            return false
        }
    }

    private readonly checkForAIContent = async (): Promise<AIDetectorDetailsType | undefined> => {
        const parametersData = this.getParametersData()
        const data = parametersData['text']

        if (!data?.value) {
            return
        }
        const text = convert(
            data?.key === BlockStateFormatKey.Markdown
                ? convertMarkdownToHTML(data.value as string)
                : (data.value as string),
            { wordwrap: false }
        )?.replace(/\n+/g, '. ')
        if (!text) {
            return
        }

        const words = text.split(' ').filter(w => !!w.trim()).length
        this.uiElement?.updater.forceUpdate()
        const hasConfigViolations = this.handleViolation(words)
        if (hasConfigViolations) {
            return
        }

        const requestPayload = {
            taskPluginId: this.config.id,
            text: text,
        }

        const resp = await checkForAIContentApi(requestPayload)

        if (resp.isErr()) {
            if (
                (resp.error.response?.data as { error: { message: string } })?.error?.message ===
                'plugin usage limit exhausted.'
            ) {
                this.studio?.workbench.enqueueUiUpdate(ui => {
                    ui.notifier.notification.error({
                        message: 'Limit exceeded',
                        description: 'You have reached the maximum limit for AI Detection',
                    })
                })
                this.checksMade = this.config.config.limit
            } else {
                this.studio?.workbench.enqueueUiUpdate(ui => {
                    ui.notifier.notification.error({
                        message: 'Failed to check for AI Content',
                        description: 'Please try again',
                    })
                })
            }
            return
        }

        const aiDetectionResponse = resp.value
        const aiContentDetails: AIDetectorDetailsType = {
            percentage: aiDetectionResponse.ai_score,
            aiTexts: aiDetectionResponse.probable_ai_texts,
        }

        this.checksMade++
        return aiContentDetails
    }
}
